import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * BW19 PR-E — action_item queries + mutations.
 *
 * Reads via the RLS-protected supabase client, exposes mutations for
 * state transitions and inline edits. Keys are scoped per-user.
 */

export const ACTION_STATES = [
  "open",
  "in_progress",
  "blocked",
  "done",
  "cancelled",
  "deferred",
] as const;
export type ActionState = (typeof ACTION_STATES)[number];

export const ACTION_CATEGORIES = [
  "travel",
  "correspondence",
  "preparation",
  "errand",
  "decision",
  "research",
  "admin",
  "communication",
  "health",
  "financial",
] as const;
export type ActionCategory = (typeof ACTION_CATEGORIES)[number];

export type ActionItem = {
  id: string;
  title: string;
  description: string | null;
  category: ActionCategory;
  state: ActionState;
  blocker_reason: string | null;
  effort_estimate_min: number | null;
  effort_actual_min: number | null;
  due_at: string | null;
  scheduled_at: string | null;
  done_at: string | null;
  capture_id: string | null;
  parent_action_id: string | null;
  project_id: string | null;
  thread_id: string | null;
  created_at: string;
  updated_at: string;
};

export function useActionItems(opts?: { includeDone?: boolean; includeCancelled?: boolean }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const includeDone = opts?.includeDone ?? false;
  const includeCancelled = opts?.includeCancelled ?? false;

  return useQuery<ActionItem[]>({
    queryKey: ["action-items", userId, includeDone, includeCancelled],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return [];
      let q = supabase
        .from("action_item")
        .select(
          "id, title, description, category, state, blocker_reason, effort_estimate_min, effort_actual_min, due_at, scheduled_at, done_at, capture_id, parent_action_id, project_id, thread_id, created_at, updated_at",
        )
        .eq("user_id", userId)
        .order("due_at", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });
      const visible: ActionState[] = ["open", "in_progress", "blocked", "deferred"];
      if (includeDone) visible.push("done");
      if (includeCancelled) visible.push("cancelled");
      q = q.in("state", visible);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ActionItem[];
    },
  });
}

export function useActionItem(id: string | undefined) {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery<ActionItem | null>({
    queryKey: ["action-item", id, userId],
    enabled: Boolean(userId && id),
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("action_item")
        .select(
          "id, title, description, category, state, blocker_reason, effort_estimate_min, effort_actual_min, due_at, scheduled_at, done_at, capture_id, parent_action_id, project_id, thread_id, created_at, updated_at",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data as ActionItem | null) ?? null;
    },
  });
}

export function useUpdateActionItem() {
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async (args: {
      id: string;
      patch: Partial<
        Pick<
          ActionItem,
          | "state"
          | "title"
          | "description"
          | "category"
          | "blocker_reason"
          | "effort_estimate_min"
          | "effort_actual_min"
          | "due_at"
          | "scheduled_at"
          | "done_at"
        >
      >;
    }) => {
      const patch: Partial<ActionItem> = { ...args.patch };
      if (patch.state === "done" && patch.done_at == null) {
        patch.done_at = new Date().toISOString();
      } else if (patch.state && patch.state !== "done") {
        patch.done_at = null;
      }
      const { error } = await supabase
        .from("action_item")
        .update(patch)
        .eq("id", args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["action-items", userId] });
      void qc.invalidateQueries({ queryKey: ["action-item"] });
    },
  });
}

export function nextStateInCycle(state: ActionState): ActionState {
  switch (state) {
    case "open":
      return "in_progress";
    case "in_progress":
      return "done";
    case "done":
      return "open";
    case "blocked":
      return "in_progress";
    case "deferred":
      return "open";
    case "cancelled":
      return "open";
  }
}
